import { createClient } from '@supabase/supabase-js';
import {
  ENV,
  readSupabasePublishableFromBuildEnv,
  readSupabaseUrlFromBuildEnv,
} from '../env';
import { LEARNER_TABLES } from './tables';
import { apiUrl } from '../../utils/appOrigin';

function readBuildTimeConfig() {
  return {
    url: readSupabaseUrlFromBuildEnv(),
    publishable: readSupabasePublishableFromBuildEnv(),
  };
}

let supabaseUrl = '';
let supabasePublishableKey = '';
let supabaseClient = null;

/** @type {Set<(client: import('@supabase/supabase-js').SupabaseClient) => void>} */
const clientReadyListeners = new Set();

/**
 * Register a callback when the Supabase client is created or recreated.
 * @param {(client: import('@supabase/supabase-js').SupabaseClient) => void} listener
 * @returns {() => void}
 */
export function onSupabaseClientReady(listener) {
  const client = getSupabase();
  if (client) listener(client);
  clientReadyListeners.add(listener);
  return () => {
    clientReadyListeners.delete(listener);
  };
}

function notifySupabaseClientReady() {
  const client = getSupabase();
  if (!client) return;
  clientReadyListeners.forEach((listener) => {
    try {
      listener(client);
    } catch (err) {
      console.warn('[supabase] client ready listener failed:', err);
    }
  });
}

function createSupabaseClient() {
  return createClient(
    supabaseUrl || 'https://placeholder.supabase.co',
    supabasePublishableKey || 'placeholder-key',
    {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    }
  );
}

function syncClient() {
  if (supabaseUrl && supabasePublishableKey) {
    supabaseClient = createSupabaseClient();
    notifySupabaseClientReady();
  } else {
    supabaseClient = null;
  }
  return supabaseClient;
}

const buildTime = readBuildTimeConfig();
if (buildTime.url && buildTime.publishable) {
  supabaseUrl = buildTime.url;
  supabasePublishableKey = buildTime.publishable;
  syncClient();
}

/** @returns {import('@supabase/supabase-js').SupabaseClient | null} */
export function getSupabase() {
  return supabaseClient ?? syncClient();
}

/** @type {import('@supabase/supabase-js').SupabaseClient} */
export const supabase = new Proxy(
  {},
  {
    get(_target, prop) {
      const client = getSupabase();
      if (!client) {
        throw new Error('Supabase is not configured');
      }
      const value = client[prop];
      return typeof value === 'function' ? value.bind(client) : value;
    },
  }
);

/**
 * Apply credentials fetched at runtime (e.g. from /api/public-config on Vercel).
 * @param {string} url
 * @param {string} publishable
 * @returns {boolean}
 */
export function applySupabaseRuntimeConfig(url, publishable) {
  const nextUrl = url?.trim();
  const nextKey = publishable?.trim();
  if (!nextUrl || !nextKey) return false;
  if (supabaseUrl === nextUrl && supabasePublishableKey === nextKey && supabaseClient) {
    return true;
  }
  supabaseUrl = nextUrl;
  supabasePublishableKey = nextKey;
  syncClient();
  return true;
}

/** @returns {boolean} */
export function isSupabaseConfigured() {
  return Boolean(supabaseUrl && supabasePublishableKey);
}

let bootstrapPromise = null;

/** Load Supabase config from build-time env or /api/public-config. */
export function bootstrapSupabaseConfig() {
  if (isSupabaseConfigured()) {
    return Promise.resolve(isSupabaseConfigured());
  }
  if (bootstrapPromise) return bootstrapPromise;

  bootstrapPromise = fetch(apiUrl('/api/public-config'))
    .then(async (res) => {
      if (!res.ok) return false;
      const data = await res.json();
      if (!data?.supabaseUrl || !data?.supabasePublishable) return false;
      return applySupabaseRuntimeConfig(data.supabaseUrl, data.supabasePublishable);
    })
    .catch(() => false)
    .finally(() => {
      bootstrapPromise = null;
    });

  return bootstrapPromise;
}

/**
 * Quick connectivity probe - verifies env + auth endpoint + optional profile read.
 * @returns {Promise<{ ok: boolean; configured: boolean; authenticated: boolean; profileReadable: boolean; email: string | null; error: string | null }>}
 */
export async function checkSupabaseConnectivity() {
  await bootstrapSupabaseConfig();

  if (!isSupabaseConfigured()) {
    return {
      ok: false,
      configured: false,
      authenticated: false,
      profileReadable: false,
      email: null,
      error: `Missing Supabase URL or publishable key. Set ${ENV.supabaseUrl} and ${ENV.supabasePublishable}, then restart the dev server (local) or redeploy (Vercel).`,
    };
  }

  const client = getSupabase();
  if (!client) {
    return {
      ok: false,
      configured: false,
      authenticated: false,
      profileReadable: false,
      email: null,
      error: 'Supabase client failed to initialize.',
    };
  }

  try {
    const { data: sessionData, error: sessionError } = await client.auth.getSession();
    if (sessionError) {
      return {
        ok: false,
        configured: true,
        authenticated: false,
        profileReadable: false,
        email: null,
        error: sessionError.message,
      };
    }

    const user = sessionData.session?.user ?? null;
    if (!user) {
      return {
        ok: true,
        configured: true,
        authenticated: false,
        profileReadable: false,
        email: null,
        error: null,
      };
    }

    const { data: profile, error: profileError } = await client
      .from(LEARNER_TABLES.studentProfiles)
      .select('id, display_name')
      .eq('id', user.id)
      .maybeSingle();

    if (profileError) {
      return {
        ok: false,
        configured: true,
        authenticated: true,
        profileReadable: false,
        email: user.email ?? null,
        error: `Auth OK but profile read failed: ${profileError.message}. Did you run the SQL migration?`,
      };
    }

    return {
      ok: true,
      configured: true,
      authenticated: true,
      profileReadable: Boolean(profile),
      email: user.email ?? null,
      error: profile ? null : 'Logged in but no stud_profiles row - check signup trigger.',
    };
  } catch (err) {
    return {
      ok: false,
      configured: true,
      authenticated: false,
      profileReadable: false,
      email: null,
      error: err instanceof Error ? err.message : 'Unknown connectivity error',
    };
  }
}
